import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { type Descendant, type Editor, Node } from "slate";

import { TrashIcon } from "../../../../components/icons/TrashIcon";
import { SlateEditor } from "../../../../components/SlateEditor/SlateEditor";
import type { MessageType } from "../../../../hooks/reactQueryHooks/useProxy";
import { CacheTypeTag } from "../CacheTypeTag/CacheTypeTag";
import { HitTypeTag } from "../HitTypeTag";
import { Latency } from "../Latency";
import { SimilarityScore } from "../SimilarityScore";
import { TokensPerSecond } from "../TokensPerSecond/TokensPerSecond";

interface MessageProps {
  message: MessageType;
  isLoading: boolean;
  deleteMessage: (id: number | string) => void;
  editMessage: (content: string, id: number | string) => void;
}

const Message: React.FC<MessageProps> = ({
  message,
  isLoading,
  deleteMessage,
  editMessage,
}) => {
  const [defaultValue, setDefaultValue] = useState<Descendant[]>([
    { type: "paragraph", children: [{ text: message.message.content || "" }] },
  ]);
  const editorRef = useRef<Editor>(null);

  useEffect(() => {
    if (message.message.content) {
      setDefaultValue([
        { type: "paragraph", children: [{ text: message.message.content }] },
      ]);
    }
  }, [message]);

  const handleEditMessage = (value: Descendant[] | null) => {
    if (editorRef.current) {
      editMessage(value ? Node.string(editorRef.current) : "", message.id);
    }
  };

  return (
    <div className="playground-message">
      <div className="playground-message-header">
        <div className="playground-message-role">{message.message.role}</div>
        <div className="playground-message-controls">
          <HitTypeTag
            type={
              message.message.role === "assistant" &&
              (message.headers?.cacheType === "no-cache" ||
                !message.headers?.xCache)
                ? "miss"
                : message.headers?.xCache || ""
            }
          />
          <CacheTypeTag type={message.headers?.cacheType || ""} />
          <SimilarityScore
            scoreType={message.headers?.hitType || ""}
            score={message.headers?.similarityScore || ""}
          />
          <Latency latency={message.headers?.latency || null} />
          <TokensPerSecond
            tokensPerSecond={message.headers?.tokensPerSecond || null}
          />
          <Button
            variant="outline-light"
            data-testid="delete"
            onClick={() => deleteMessage(message.id)}
          >
            <TrashIcon />
          </Button>
        </div>
      </div>

      <SlateEditor
        ref={editorRef}
        isLoading={false}
        defaultValue={defaultValue}
        accessAllowed={true}
        setValue={handleEditMessage}
        inPlayground
        placeholder={`Enter ${message.message.role} message`}
        isReadOnly={isLoading}
      />
    </div>
  );
};

export { Message };
